/**
 * @file src/_modulos/prenota/tabela/layouts/config/store/types.ts
 * @description Store unificado simplificado para tabela de pré-notas
 * 
 * Princípios aplicados:
 * - SRP: Cada store tem uma responsabilidade única
 * - KISS: Lógica simples e direta
 * - YAGNI: Remove funcionalidades não utilizadas
 */

"use client";

import { persist, createJSONStorage, devtools } from "zustand/middleware";
import { create, StateCreator } from "zustand";

/* -------------------------------------------------------------------------- */
/* TIPOS BASE                                                                 */
/* -------------------------------------------------------------------------- */

export interface TablePagination {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface TableFilters {
  [key: string]: any;
}

export interface TableSorting {
  id: string;
  desc: boolean;
}

export interface TableState {
  // Paginação
  pageIndex: number;
  pageSize: number;
  pagination: TablePagination;

  // Busca e filtros
  searchTerm: string;
  searchField: string; // Campo específico para busca ("" = todos)
  appliedFilters: TableFilters;

  // Filiais
  selectedFilials: string[];
  accessibleFilials: string[];

  // Ordenação
  sorting: TableSorting[];

  // Tab ativa
  activeTab: string;
}

export interface TableActions {
  // Paginação
  setPagination: (pagination: TablePagination) => void;
  setPageIndex: (pageIndex: number) => void;
  setPageSize: (pageSize: number) => void;

  // Busca
  setSearchTerm: (searchTerm: string) => void;
  setSearchField: (searchField: string) => void;

  // Filtros
  setAppliedFilters: (filters: TableFilters) => void;
  clearAppliedFilters: () => void;
  setFilter: (field: string, value: any) => void;
  removeFilter: (field: string) => void;

  // Filiais
  setSelectedFilials: (filials: string[]) => void;
  clearSelectedFilials: () => void;
  setAccessibleFilials: (filials: string[]) => void;

  // Ordenação
  setSorting: (sorting: TableSorting[]) => void;
  resetSorting: () => void;

  // Tabs
  setActiveTab: (tab: string) => void;

  // Utilitários
  clearAll: () => void;
  hasActiveFilters: () => boolean;
}

export type TableStore = TableState & TableActions;

/* -------------------------------------------------------------------------- */
/* ESTADO INICIAL                                                             */
/* -------------------------------------------------------------------------- */

const initialState: TableState = {
  pageIndex: 0,
  pageSize: 10,
  pagination: { page: 1, pageSize: 10, totalCount: 0, totalPages: 0 },
  searchTerm: "",
  searchField: "", // "" = busca em todos os campos
  appliedFilters: {},
  selectedFilials: [],
  accessibleFilials: [],
  sorting: [],
  activeTab: "todas",
};

/* -------------------------------------------------------------------------- */
/* STORE PRINCIPAL                                                            */
/* -------------------------------------------------------------------------- */

const createTableStore: StateCreator<
  TableStore,
  [["zustand/persist", unknown], ["zustand/devtools", unknown]]
> = (set, get) => ({
  ...initialState,

  // Paginação
  setPagination: (pagination) =>
    set({
      pagination,
      pageIndex: pagination.page - 1,
      pageSize: pagination.pageSize,
    }),

  setPageIndex: (pageIndex) =>
    set({
      pageIndex,
      pagination: { ...get().pagination, page: pageIndex + 1 },
    }),

  setPageSize: (pageSize) =>
    set({
      pageSize,
      pageIndex: 0,
      pagination: { ...get().pagination, pageSize, page: 1 },
    }),

  // Busca
  setSearchTerm: (searchTerm) =>
    set({
      searchTerm,
      pageIndex: 0,
      pagination: { ...get().pagination, page: 1 },
    }),

  setSearchField: (searchField) =>
    set({
      searchField,
      pageIndex: 0,
      pagination: { ...get().pagination, page: 1 },
    }),

  // Filtros
  setAppliedFilters: (filters) =>
    set({
      appliedFilters: filters,
      pageIndex: 0,
      pagination: { ...get().pagination, page: 1 },
    }),

  clearAppliedFilters: () =>
    set({
      appliedFilters: {},
      pageIndex: 0,
      pagination: { ...get().pagination, page: 1 },
    }),

  setFilter: (field, value) =>
    set((state) => ({
      appliedFilters: { ...state.appliedFilters, [field]: value },
      pageIndex: 0,
      pagination: { ...get().pagination, page: 1 },
    })),

  removeFilter: (field) =>
    set((state) => {
      const { [field]: removed, ...remainingFilters } = state.appliedFilters;
      return {
        appliedFilters: remainingFilters,
        pageIndex: 0,
        pagination: { ...get().pagination, page: 1 },
      };
    }),

  // Filiais
  setSelectedFilials: (filials) =>
    set({
      selectedFilials: filials,
      pageIndex: 0,
      pagination: { ...get().pagination, page: 1 },
    }),

  clearSelectedFilials: () =>
    set({
      selectedFilials: [],
      pageIndex: 0,
      pagination: { ...get().pagination, page: 1 },
    }),

  setAccessibleFilials: (filials) => set({ accessibleFilials: filials }),

  // Ordenação
  setSorting: (sorting) => set({ sorting }),
  resetSorting: () => set({ sorting: [] }),

  // Tabs
  setActiveTab: (tab) => {
    const reset = tab === "todas" ? {
      appliedFilters: {},
      searchTerm: "",
      searchField: "",
      selectedFilials: [],
      pageIndex: 0,
      pagination: { ...get().pagination, page: 1 },
    } : {};
    set({ activeTab: tab, ...reset });
  },

  // Utilitários
  clearAll: () => set({
    appliedFilters: {},
    searchTerm: "",
    searchField: "",
    selectedFilials: [],
    pageIndex: 0,
    sorting: [],
    pagination: { ...get().pagination, page: 1 },
  }),

  hasActiveFilters: () => {
    const { appliedFilters } = get();
    return Object.keys(appliedFilters).length > 0;
  },
});

export const useTableStore = create<TableStore>()(
  persist(
    devtools(createTableStore, {
      name: "table-store",
    }),
    {
      name: "table-store",
      storage: createJSONStorage(() => localStorage),
    }
  )
);

/* -------------------------------------------------------------------------- */
/* HOOKS ESPECIALIZADOS                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Hook para acessar apenas o estado
 */
export const useTableState = () => {
  const store = useTableStore();
  return {
    pageIndex: store.pageIndex,
    pageSize: store.pageSize,
    pagination: store.pagination,
    searchTerm: store.searchTerm,
    appliedFilters: store.appliedFilters,
    selectedFilials: store.selectedFilials,
    accessibleFilials: store.accessibleFilials,
    sorting: store.sorting,
    activeTab: store.activeTab,
  };
};

/**
 * Hook para acessar apenas as ações
 */
export const useTableActions = () => {
  const store = useTableStore();
  return {
    setPagination: store.setPagination,
    setPageIndex: store.setPageIndex,
    setPageSize: store.setPageSize,
    setSearchTerm: store.setSearchTerm,
    setAppliedFilters: store.setAppliedFilters,
    clearAppliedFilters: store.clearAppliedFilters,
    setFilter: store.setFilter,
    removeFilter: store.removeFilter,
    setSelectedFilials: store.setSelectedFilials,
    clearSelectedFilials: store.clearSelectedFilials,
    setAccessibleFilials: store.setAccessibleFilials,
    setSorting: store.setSorting,
    resetSorting: store.resetSorting,
    setActiveTab: store.setActiveTab,
    clearAll: store.clearAll,
    hasActiveFilters: store.hasActiveFilters,
  };
};

/**
 * Hook para verificar estados de visibilidade
 */
export const useTableVisibility = () => {
  const { appliedFilters, selectedFilials, accessibleFilials, sorting } = useTableState();

  return {
    hasActiveFilters: Object.keys(appliedFilters).length > 0,
    hasFilialFilter: selectedFilials.length > 0 && selectedFilials.length !== accessibleFilials.length,
    hasSorting: sorting.length > 0,
  };
};
